/**
 * PATH: packages/job-hub/src/jobs/qa/QaRunItemActions.tsx
 * FILE_ID: JOBHUB-QA-RUN-ITEM-ACTIONS-001
 * PURPOSE: Run-mode add/edit/delete + pending request controls for a QA row.
 * WHY: JobQaExecution.tsx is near the 500-line ceiling; new UI lives here.
 */
import { useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { QaItemChangeBanner } from '@quotech/ui'
import type { QaApi, QaItemEdit, QaItemInstance } from './qaApi'
import { isPendingQaChange, QA_PENDING_DELETE_NOTICE } from '../tabs/job-qa/qaTabSelectors'

export function QaRunItemActions({
  item,
  areaId,
  api,
  onMutated,
  disabled = false,
  showEditActions = true,
}: {
  item: QaItemInstance
  areaId: string
  api: QaApi
  onMutated: () => void
  disabled?: boolean
  showEditActions?: boolean
}) {
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [title, setTitle] = useState(item.title)
  const canApprove = Boolean(item.can_verify_completion)
  const pending = item.pending_change_id
    ? { id: item.pending_change_id, kind: item.pending_change_kind ?? 'edit' }
    : null

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      onMutated()
    } finally {
      setBusy(false)
    }
  }

  const submitEdit = () => {
    const fields: QaItemEdit = { title }
    void run(async () => {
      if (canApprove && item.template_item) {
        await api.updateItem(item.template_item, fields)
      } else {
        await api.raiseItemChange(item.id, 'edit', fields)
      }
      setEditing(false)
    })
  }

  return (
    <div className="mt-2 space-y-1.5">
      {notice && <p role="status" className="text-[11px] text-muted-foreground">{notice}</p>}
      {pending && (
        <QaItemChangeBanner
          kind={pending.kind}
          canApprove={canApprove}
          busy={busy}
          onApprove={() => run(() => api.approveItemChange(pending.id))}
          onReject={() => run(() => api.rejectItemChange(pending.id))}
        />
      )}
      {item.revised && !pending && (
        <p className="text-[11px] text-muted-foreground">Revised after issue.</p>
      )}
      {showEditActions && !disabled && !pending && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(() => api.addItem(areaId, {}, null, item.item_number))}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-[11px] text-muted-foreground hover:text-primary"
          >
            <Plus className="h-3 w-3" aria-hidden /> Add below
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setEditing((open) => !open)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <Pencil className="h-3 w-3" aria-hidden /> Edit
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(async () => {
              const result = await api.deleteItem(item.id)
              if (isPendingQaChange(result)) setNotice(QA_PENDING_DELETE_NOTICE)
            })}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" aria-hidden /> Delete
          </button>
        </div>
      )}
      {editing && (
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            aria-label={`Edit title for ${item.item_number}`}
            className="min-h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs"
          />
          <button
            type="button"
            disabled={busy || !title.trim()}
            onClick={submitEdit}
            className="rounded-md bg-primary px-2 py-1 text-[11px] text-primary-foreground"
          >
            {canApprove ? 'Save' : 'Request'}
          </button>
        </div>
      )}
    </div>
  )
}
